/**
 * Spec 014a Phase D / 014b: copy and rules for the "Host permissions" admin
 * card (7 page and trip switches; car settings are in `carSettingsAdmin.ts`).
 * Plain functions, tested in `hostCapabilitiesAdmin.test.ts`.
 */
import {
  HostCapabilities,
  STORED_CAPABILITY_KEYS,
  StoredCapabilityKey,
} from '../../utils/hostCapabilities';
import { CANCELLATION_RULE_TITLES, describeCancellationValue } from './cancellationRulesAdmin';
import { CAR_SETTING_TITLES, CarSettingRuleKey, describeCarSetting } from './carSettingsAdmin';
import { describeServiceValue, SERVICE_RULE_TITLES } from './serviceRulesAdmin';
import { describeBookingRuleValue } from './bookingRulesAdmin';
import { describeRegionValue, REGION_TITLES } from './regionAdmin';

export interface CapabilityInfo {
  key: StoredCapabilityKey;
  group: 'pages' | 'trips';
  title: string;
  /** What hosts lose when it is off. */
  hides: string;
  /**
   * Set when nothing replaces the host's action yet (plan.md readiness
   * table): turning it off asks for confirmation and says what stops.
   */
  notReady?: string;
}

export const CAPABILITY_INFO: readonly CapabilityInfo[] = [
  {
    key: 'tripFees',
    group: 'pages',
    title: 'Trip fees',
    hides: 'The Trip fees page.',
  },
  {
    key: 'insurance',
    group: 'pages',
    title: 'Protection plans',
    hides: 'The Protection plans page.',
  },
  {
    key: 'couponsAndDealerTools',
    group: 'pages',
    title: 'Coupons and dealer tools',
    hides: 'Coupons, product types, dealership pages and Manage ShortID.',
  },
  {
    key: 'bookingActions',
    group: 'trips',
    title: 'Trip changes',
    hides:
      'Cancel or modify a trip without penalty, update minimum rental days, swap cars, force a swap decision.',
  },
  {
    key: 'refundReview',
    group: 'trips',
    title: 'Refund review',
    hides: 'Refund invoices page and the buttons that send a refund.',
    notReady:
      'Nobody else can approve refunds yet, so guest refunds will wait until you turn this back on.',
  },
  {
    key: 'incidentAmounts',
    group: 'trips',
    title: 'Incident charges',
    hides: 'Charge for incidents on a trip.',
    notReady:
      'There are no fixed incident fees yet, so hosts will not be able to charge for incidents.',
  },
  {
    key: 'licenceReview',
    group: 'trips',
    title: 'Guest licence review',
    hides: "Reviewing a guest's driver's licence on a trip.",
    notReady:
      'Admins cannot review licences yet, so guests waiting for review will not be able to start their trips.',
  },
];

export const CAPABILITY_GROUPS: ReadonlyArray<{ group: CapabilityInfo['group']; title: string }> = [
  { group: 'pages', title: 'Pages' },
  { group: 'trips', title: 'Trips' },
];

/** Only the switches that differ from what was loaded. */
export function changedCapabilities(
  loaded: HostCapabilities,
  edited: HostCapabilities,
): Partial<Record<StoredCapabilityKey, boolean>> {
  return STORED_CAPABILITY_KEYS.reduce<Partial<Record<StoredCapabilityKey, boolean>>>(
    (acc, key) => {
      if (loaded[key] !== edited[key]) acc[key] = edited[key];
      return acc;
    },
    {},
  );
}

const TITLE_BY_KEY: Record<string, string> = {
  ...Object.fromEntries(CAPABILITY_INFO.map(info => [info.key, info.title])),
  ...CANCELLATION_RULE_TITLES,
  ...CAR_SETTING_TITLES,
  ...SERVICE_RULE_TITLES,
  // Spec 014a's grouped switches, in history rows written before 014b.
  carPricing: 'Discounts and special pricing',
  carRules: 'Rental rules',
  protectionAndFeesPages: 'Protection plans and trip fees',
  minimumAge: 'Minimum driver age',
  uniformDailyRate: 'One daily rate',
  requireRecallCheck: 'Recall check before booking',
  requireCardholderNameMatch: 'Cardholder name must match',
  depositCollection: 'Deposit collection',
  depositReleaseBusinessDays: 'Deposit release',
  carCardPrice: 'Car card price',
  bookingsOpen: 'Bookings',
  // Spec 028.
  ...REGION_TITLES,
};

/** Rules that read as something other than "not set" when the key is missing. */
const DEFAULT_WHEN_MISSING: Record<string, unknown> = {
  // Spec 013 P7: the cardholder name check is on unless a brand turns it off.
  requireCardholderNameMatch: true,
  // Spec 023: a brand takes bookings unless the platform owner closes it.
  bookingsOpen: true,
  // Spec 028: today's region.
  country: 'US',
  currency: 'USD',
  measurementSystem: 'imperial',
};

function display(stored: unknown, key?: string, unit: 'mi' | 'km' = 'mi'): string {
  const value =
    (stored === null || stored === undefined) && key && key in DEFAULT_WHEN_MISSING
      ? DEFAULT_WHEN_MISSING[key]
      : stored;
  const described = key
    ? (describeRegionValue(key, value) ??
      describeCancellationValue(key, value) ??
      describeServiceValue(key, value) ??
      describeBookingRuleValue(key, value))
    : null;
  if (described !== null) return described;
  if (key && key in CAR_SETTING_TITLES) {
    return describeCarSetting(key as CarSettingRuleKey, value, unit) ?? 'set by host';
  }
  if (value === true) return 'on';
  if (value === false) return 'off';
  if (value === null || value === undefined) return 'not set';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * One line per changed key for the history list, e.g.
 * "Discounts and special pricing: on → off". A row with no `before` was
 * created then (the migration, or the first save).
 */
export function describeRulesChange(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
): string[] {
  if (!before) {
    return ['Created'];
  }
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after ?? {})])).sort();
  // Spec 028: each side's distance rules are in that side's unit.
  const unitOf = (rules: Record<string, unknown> | null | undefined) =>
    rules?.measurementSystem === 'metric' ? 'km' : 'mi';
  return keys
    .filter(key => JSON.stringify(before[key]) !== JSON.stringify(after?.[key]))
    .map(
      key =>
        `${TITLE_BY_KEY[key] ?? key}: ${display(before[key], key, unitOf(before))} → ${display(after?.[key], key, unitOf(after))}`,
    );
}
